import React, { useEffect, useRef, useState } from "react";
import { fetchLfg, createLfg, deleteLfg, fetchGames, requestFriend, fetchFriendStatus, fetchMyLeagues } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AvatarChip } from "./AvatarChip";
import { DmModal } from "./DmModal";
import { PlatformChips } from "./PlatformChips";
import { useFormDraft, clearFormDraft } from "../hooks/useFormDraft";

const LOOKING_FOR = ["Team", "Players", "Sub"];

function buildLfgWsUrl() {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
  try {
    const u = new URL(`${BACKEND_URL}/api/ws/lfg`);
    u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
    return u.toString();
  } catch { return null; }
}

export function LfgSection({ onToast, onRequireAuth, onOpenLeague }) {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [games, setGames] = useState([]);
  const [myLeagues, setMyLeagues] = useState([]);
  const [filterGame, setFilterGame] = useState("");
  const [filterLF, setFilterLF] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    game: "", looking_for: "Team", position: "", message: "", league_id: "",
  });
  const [friendStatus, setFriendStatus] = useState({});
  const [busyFriendId, setBusyFriendId] = useState("");
  const [dmTarget, setDmTarget] = useState(null);

  // LFG composer "message" is auto-drafted to localStorage so a misclick
  // refresh never costs the player a written-out blurb.
  const lfgKey = user ? `lfg-post:${user.user_id}` : "lfg-post:anon";
  useFormDraft(lfgKey, form.message, (v) => setForm((f) => ({ ...f, message: v })));

  useEffect(() => {
    fetchGames().then((g) => {
      setGames(g);
      // Seed the form default to the first available game so the dropdown
      // never renders empty (the "League of Legends" default was bogus if
      // that game isn't in the catalog).
      setForm((f) => f.game ? f : { ...f, game: (g[0]?.name || "") });
    });
  }, []);

  // My-leagues for the broadcast picker — only show leagues the user is a
  // member of so spam to random leagues isn't possible.
  useEffect(() => {
    if (!user) { setMyLeagues([]); return; }
    fetchMyLeagues().then(setMyLeagues).catch(() => setMyLeagues([]));
  }, [user]);

  // Whenever the user picks a league, auto-set the game to that league's game
  // (so the LFG signal matches the league's competitive scope).
  useEffect(() => {
    if (!form.league_id) return;
    const lg = myLeagues.find((l) => l.league_id === form.league_id);
    if (lg && lg.game && lg.game !== form.game) {
      setForm((f) => ({ ...f, game: lg.game }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.league_id, myLeagues]);

  const load = async () => {
    const q = {};
    if (filterGame) q.game = filterGame;
    if (filterLF) q.looking_for = filterLF;
    setPosts(await fetchLfg(q));
  };
  useEffect(() => { load(); }, [filterGame, filterLF]);

  // Real-time LFG wall — listen for new/deleted posts via /api/ws/lfg and
  // prepend the matching ones without a full reload. Filters are applied
  // client-side so the badge counts stay in sync.
  const wsRef = useRef(null);
  const filterGameRef = useRef(filterGame);
  const filterLFRef = useRef(filterLF);
  useEffect(() => { filterGameRef.current = filterGame; }, [filterGame]);
  useEffect(() => { filterLFRef.current = filterLF; }, [filterLF]);
  const [liveBlip, setLiveBlip] = useState(0);  // ticks each time a new post arrives
  useEffect(() => {
    const url = buildLfgWsUrl();
    if (!url) return;
    let closed = false;
    let backoff = 1000;
    const connect = () => {
      if (closed) return;
      let ws;
      try { ws = new WebSocket(url); } catch { schedule(); return; }
      wsRef.current = ws;
      ws.onopen = () => { backoff = 1000; };
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type === "lfg:new" && data.post) {
            const p = data.post;
            const fg = filterGameRef.current;
            const fl = filterLFRef.current;
            if (fg && p.game !== fg) return;
            if (fl && p.looking_for !== fl) return;
            setPosts((prev) => {
              if (prev.some((x) => x.post_id === p.post_id)) return prev;
              return [p, ...prev].slice(0, 50);
            });
            setLiveBlip((n) => n + 1);
          } else if (data.type === "lfg:deleted" && data.post_id) {
            setPosts((prev) => prev.filter((x) => x.post_id !== data.post_id));
          }
        } catch { /* ignore */ }
      };
      ws.onerror = () => { /* let onclose reconnect */ };
      ws.onclose = () => { schedule(); };
    };
    const schedule = () => {
      if (closed) return;
      const d = Math.min(15000, backoff);
      backoff = Math.min(15000, backoff * 2);
      setTimeout(connect, d);
    };
    connect();
    const pingId = setInterval(() => {
      try { if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify({ type: "ping" })); } catch { /* ignore */ }
    }, 25000);
    return () => { closed = true; clearInterval(pingId); try { wsRef.current?.close(); } catch { /* ignore */ } };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return onRequireAuth();
    if (!form.game) return onToast("Pick a game first.");
    if (!form.message.trim()) return onToast("Add a short message.");
    try {
      await createLfg({
        game: form.game,
        looking_for: form.looking_for,
        position: form.position,
        message: form.message,
        ...(form.league_id ? { league_id: form.league_id } : {}),
      });
      onToast("📡 LFG signal broadcasted.");
      setOpen(false);
      setForm({
        game: form.game, looking_for: "Team", position: "", message: "", league_id: "",
      });
      clearFormDraft(lfgKey);
      load();
    } catch (err) {
      onToast(err?.response?.data?.detail || "Could not post LFG.");
    }
  };

  const del = async (id) => {
    await deleteLfg(id);
    onToast("Post removed.");
    load();
  };

  // Load friend status for all LFG authors (so the SQUAD UP button shows the
  // right state). Skips self-posts.
  useEffect(() => {
    if (!user || !posts.length) return;
    const others = posts.filter((p) => p.user_id !== user.user_id);
    Promise.all(
      others.map((p) =>
        fetchFriendStatus(p.user_id)
          .then((s) => [p.user_id, s.status])
          .catch(() => [p.user_id, "none"])
      )
    ).then((pairs) => {
      const map = {};
      for (const [uid, st] of pairs) map[uid] = st;
      setFriendStatus(map);
    });
  }, [user, posts]);

  const sendFriend = async (p) => {
    if (!user) { onRequireAuth?.(); return; }
    setBusyFriendId(p.user_id);
    try {
      const r = await requestFriend(p.user_id);
      const next = r.status === "accepted" ? "friends" : "request_sent";
      setFriendStatus((prev) => ({ ...prev, [p.user_id]: next }));
      onToast?.(r.status === "accepted"
        ? `🤝 You and ${p.user_name} are now friends.`
        : `📨 Friend request sent to ${p.user_name}.`);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Could not send friend request.");
    } finally { setBusyFriendId(""); }
  };

  const openDm = (p) => {
    if (!user) { onRequireAuth?.(); return; }
    setDmTarget({
      user_id: p.user_id,
      name: p.user_name,
      avatar_id: p.user_avatar_id,
      pfp_url: p.user_pfp_url,
    });
  };

  return (
    <section
      id="lfg"
      className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-20 page-z border-t border-[#14b8a6]/20"
    >
      <div className="flex justify-between items-end flex-wrap gap-6 mb-10">
        <div>
          <span className="px-4 py-1.5 text-[10px] font-bold tracking-[3px] bg-[#14b8a6]/15 text-[#14b8a6] rounded-full neon-border-[#14b8a6]">
            LFG • LOOKING FOR GROUP
          </span>
          <h2 className="text-4xl md:text-5xl font-display grad-text mt-5 flex items-center gap-3 flex-wrap">
            SQUAD UP
            <span
              data-testid="lfg-live-indicator"
              className="flex items-center gap-1.5 text-[10px] tracking-[3px] text-[#14b8a6]/80 px-2 py-1 rounded-full bg-[#14b8a6]/10 border border-[#14b8a6]/30 normal-case font-sans"
              title="New posts appear instantly — no refresh needed"
            >
              <span className={`w-1.5 h-1.5 rounded-full bg-[#14b8a6] ${liveBlip > 0 ? "animate-ping" : "animate-pulse"}`} />
              REAL-TIME
            </span>
          </h2>
          <p className="text-lg text-[#a3a3ac] mt-3 max-w-xl">
            Broadcast a signal. Find subs, teammates, or a squad to join.
          </p>
        </div>
        <button
          onClick={() => (user ? setOpen(true) : onRequireAuth())}
          data-testid="post-lfg-btn"
          className="btn-neon px-7 py-4 rounded-3xl"
        >
          POST LFG
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <select
          value={filterGame}
          onChange={(e) => setFilterGame(e.target.value)}
          data-testid="lfg-game-filter"
          className="bg-black/50 border border-[#14b8a6]/30 rounded-3xl px-5 py-3 text-white"
        >
          <option value="">ALL GAMES</option>
          {games.map((g) => <option key={g.slug} value={g.name}>{g.name}</option>)}
        </select>
        <select
          value={filterLF}
          onChange={(e) => setFilterLF(e.target.value)}
          data-testid="lfg-lf-filter"
          className="bg-black/50 border border-[#14b8a6]/30 rounded-3xl px-5 py-3 text-white"
        >
          <option value="">ALL TYPES</option>
          {LOOKING_FOR.map((l) => <option key={l}>{l}</option>)}
        </select>
      </div>

      {posts.length === 0 ? (
        <div className="glass rounded-3xl p-10 text-center">
          <p className="text-xl text-white/60">No LFG signals yet. Be the first to broadcast.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="lfg-grid">
          {posts.map((p) => (
            <div
              key={p.post_id}
              data-testid={`lfg-card-${p.post_id}`}
              className="card-3d glass rounded-3xl p-5"
            >
              <div className="flex items-start gap-3">
                <AvatarChip avatarId={p.user_avatar_id} pfpUrl={p.user_pfp_url} size={48} />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-bold">{p.user_name}</p>
                    {p.user_id === user?.user_id && (
                      <button
                        onClick={() => del(p.post_id)}
                        data-testid={`lfg-delete-${p.post_id}`}
                        className="text-xs text-[#14b8a6] hover:underline"
                      >
                        delete
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-[#a3a3ac]">
                    {p.game} • {p.looking_for} {p.position ? `• ${p.position}` : ""} • MMR {p.elo}
                  </p>
                  {p.league_id && (
                    <button
                      type="button"
                      onClick={() => onOpenLeague?.(p.league_id)}
                      data-testid={`lfg-league-chip-${p.post_id}`}
                      className="mt-1 inline-flex items-center gap-1 text-[10px] tracking-wide px-2 py-0.5 rounded-lg bg-[#14b8a6]/10 border border-[#14b8a6]/40 text-[#14b8a6] hover:bg-[#14b8a6]/20"
                    >
                      <i className="fas fa-trophy text-[9px]" />
                      <span className="truncate max-w-[200px]">{p.league_name || "League"}</span>
                    </button>
                  )}
                </div>
              </div>
              <p className="mt-3 text-white/85">{p.message}</p>
              {/* Find me on — Steam/Xbox/PSN/Epic chips snapshotted at
                  post-create time. Hidden silently when the poster
                  hasn't linked any platforms. */}
              <div className="mt-3">
                <PlatformChips
                  source={p}
                  compact
                  testIdPrefix={`lfg-platforms-${p.post_id}`}
                  onCopy={onToast}
                />
              </div>
              <div className="mt-4 flex justify-between items-center text-xs text-white/40">
                <span>{(p.user_type || "casual").toUpperCase()}</span>
                <span>{new Date(p.created_at).toLocaleString()}</span>
              </div>
              {user && p.user_id !== user.user_id && (
                <div className="mt-4 pt-3 border-t border-white/10 flex gap-2" data-testid={`lfg-squad-${p.post_id}`}>
                  <button
                    onClick={() => sendFriend(p)}
                    disabled={friendStatus[p.user_id] === "friends" || friendStatus[p.user_id] === "request_sent" || busyFriendId === p.user_id}
                    data-testid={`lfg-friend-btn-${p.post_id}`}
                    className={`flex-1 text-[11px] tracking-widest px-3 py-2 rounded-xl border ${
                      friendStatus[p.user_id] === "friends"
                        ? "bg-[#14b8a6]/15 text-[#14b8a6] border-[#14b8a6]/40"
                        : "bg-[#14b8a6]/15 text-[#14b8a6] border-[#14b8a6]/40 hover:bg-[#14b8a6]/25"
                    } disabled:opacity-60 disabled:cursor-not-allowed`}
                  >
                    {busyFriendId === p.user_id ? "..." :
                      friendStatus[p.user_id] === "friends" ? "🤝 SQUAD"
                      : friendStatus[p.user_id] === "request_sent" ? "✉ SENT"
                      : friendStatus[p.user_id] === "request_received" ? "✓ ACCEPT"
                      : "+ SQUAD UP"}
                  </button>
                  <button
                    onClick={() => openDm(p)}
                    data-testid={`lfg-msg-btn-${p.post_id}`}
                    className="flex-1 text-[11px] tracking-widest px-3 py-2 rounded-xl bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25"
                  >
                    <i className="fas fa-envelope mr-1" />MSG
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {dmTarget && (
        <DmModal
          targetUser={dmTarget}
          onClose={() => setDmTarget(null)}
          onToast={onToast}
        />
      )}

      {open && (
        <div
          className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-md p-4 flex items-center justify-center"
          onClick={() => setOpen(false)}
          data-testid="lfg-modal-backdrop"
        >
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="glass neon-glow-pink rounded-3xl p-8 w-full max-w-lg"
            data-testid="lfg-modal"
          >
            <h3 className="text-3xl font-display grad-text mb-6">BROADCAST SIGNAL</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <select
                  value={form.game}
                  onChange={(e) => setForm({ ...form, game: e.target.value })}
                  data-testid="lfg-game-select"
                  className="bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-3 text-white"
                >
                  {!form.game && <option value="">PICK A GAME…</option>}
                  {games.map((g) => <option key={g.slug} value={g.name}>{g.name}</option>)}
                </select>
                <select
                  value={form.looking_for}
                  onChange={(e) => setForm({ ...form, looking_for: e.target.value })}
                  data-testid="lfg-lf-select"
                  className="bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-3 text-white"
                >
                  {LOOKING_FOR.map((l) => <option key={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] tracking-[3px] text-[#14b8a6] mb-1">
                  <i className="fas fa-trophy mr-1" />BROADCAST INTO A LEAGUE (optional)
                </label>
                <select
                  value={form.league_id}
                  onChange={(e) => setForm({ ...form, league_id: e.target.value })}
                  data-testid="lfg-league-select"
                  className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-3 text-white"
                >
                  <option value="">📡 PUBLIC — anyone can see</option>
                  {myLeagues.map((l) => (
                    <option key={l.league_id} value={l.league_id}>
                      🏟 {l.name} · {l.game}{l.is_owner ? " · OWNER" : ""}
                    </option>
                  ))}
                </select>
                {myLeagues.length === 0 && (
                  <p className="text-[10px] text-white/40 mt-1">
                    Join or create a league first to broadcast to one.
                  </p>
                )}
              </div>
              <input
                placeholder="Position (optional)"
                value={form.position}
                onChange={(e) => setForm({ ...form, position: e.target.value })}
                data-testid="lfg-position-input"
                className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-3 text-white"
              />
              <textarea
                rows="4"
                placeholder="What are you looking for?"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="lfg-message-input"
                className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-3 text-white"
              />
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost px-5 py-3 rounded-2xl">
                Cancel
              </button>
              <button type="submit" data-testid="lfg-submit-btn" className="btn-neon px-7 py-3 rounded-2xl">
                BROADCAST
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}

export default LfgSection;
